import React from 'react';
import { Table, Tag, Button, Space, Input, Popconfirm, message, Grid, Card } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, FileExcelOutlined, DownloadOutlined } from '@ant-design/icons';
import { deleteOrder } from '../store/ordersSlice';
import { fetchBDEs } from '../store/bdeSlice';
import { fetchShops } from '../store/shopsSlice';
import { exportToExcel } from '../utils/exportUtils';
import { formatCurrency } from '../utils/formatters';
import { generateInvoicePDF } from '../utils/invoiceGenerator';
import { Select } from 'antd';
import { updateOrder } from '../store/ordersSlice';
import { addPayout } from '../store/payoutsSlice';

const Orders = () => {
  const { orders } = useSelector((state) => state.orders);
  const { users: bdes } = useSelector((state) => state.bde);
  const { shops } = useSelector((state) => state.shops);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [query, setQuery] = React.useState('');
  const [invoiceLoading, setInvoiceLoading] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState('');

  React.useEffect(() => {
    dispatch(fetchBDEs());
    dispatch(fetchShops());
  }, [dispatch]);

  const handleInvoice = async (order, shop, bde, action) => {
    try {
      setInvoiceLoading(order.id);
      await generateInvoicePDF(order, shop, bde, action);
      if (action === 'download') {
        message.success('Invoice downloaded successfully');
      }
    } catch (error) {
      message.error('Failed to generate invoice: ' + error.message);
    } finally {
      setInvoiceLoading(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteOrder(id)).unwrap();
      message.success('Order deleted successfully');
    } catch (err) {
      message.error('Failed to delete order: ' + err);
    }
  };

  const handleExport = () => {
    if (!orders || orders.length === 0) {
      message.warning('No orders to export');
      return;
    }

    const dataToExport = orders.map(o => {
      const bde = bdes.find(u => u.id === o.bdeId);
      const shop = shops.find(s => s.id === o.shopId);
      return {
        'Order ID': o.id,
        'BDE Name': bde ? bde.name : o.bdeId,
        'BDE Mobile': bde ? bde.mobile : 'N/A',
        'Shop Name': o.customerName,
        'Shop Owner': shop ? shop.ownerName : 'N/A',
        'Shop Mobile': shop ? shop.contactNumber : 'N/A',
        'Amount (₹)': o.amount,
        Status: o.status,
        'Created At': new Date(o.createdAt).toLocaleString(),
        'Commission Paid': o.commissionPaid ? 'Yes' : 'No'
      };
    });

    exportToExcel(dataToExport, 'Orders_List');
    message.success('Orders exported successfully');
  };

  const isEligibleForCommission = (status) => {
    return ['Expected', 'Processing', 'Completed'].includes(status);
  };

  const handleSinglePay = async (order) => {
    if (order.commissionPaid || !isEligibleForCommission(order.status)) {
      message.info('Commission already paid or not eligible');
      return;
    }

    const commission = Number(order.amount) * 0.10 || 0;

    if (commission <= 0) {
      message.warning('Commission is 0');
      return;
    }

    try {
      // Add payout
      await dispatch(addPayout({
        bdeId: order.bdeId,
        amount: commission,
        transactionId: `ORDER-PAY-${order.id}-${Date.now()}`,
        notes: `Commission for Order ${order.id}`
      })).unwrap();

      // Mark order paid
      await dispatch(updateOrder({
        ...order,
        commissionPaid: true
      })).unwrap();

      message.success('Commission paid successfully');
    } catch (err) {
      message.error('Payment failed: ' + err);
    }
  };



  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => String(a.id).localeCompare(String(b.id)),
    },
    {
      title: 'BDE Name',
      dataIndex: 'bdeId',
      key: 'bdeId',
      render: (bdeId) => {
        const bde = bdes.find(u => u.id === bdeId);
        return bde ? bde.name : bdeId;
      },
      sorter: (a, b) => {
        const nameA = bdes.find(u => u.id === a.bdeId)?.name || String(a.bdeId);
        const nameB = bdes.find(u => u.id === b.bdeId)?.name || String(b.bdeId);
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Shop Name',
      dataIndex: 'customerName',
      key: 'customerName',
      sorter: (a, b) => String(a.customerName || '').localeCompare(String(b.customerName || '')),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount),
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => String(a.status || '').localeCompare(String(b.status || '')),
      render: (status) => {
        let color = 'default';
        if (status === 'Completed') color = 'green';
        if (status === 'Pending') color = 'orange';
        if (status === 'Expected') color = 'gold';
        if (status === 'Processing') color = 'blue';
        if (status === 'Cancelled') color = 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => {
        const showInvoice = record.status === 'Completed';
        const shop = shops.find(s => s.id === record.shopId);
        const bde = bdes.find(u => u.id === record.bdeId);

        return (
          <Space size="middle">
            {showInvoice && (
              <>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleSinglePay(record)}
                  disabled={record.commissionPaid}
                >
                  {record.commissionPaid ? "Paid" : "Pay"}
                </Button>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  loading={invoiceLoading === record.id}
                  onClick={() => handleInvoice(record, shop, bde, 'download')}
                  title="Download Invoice"
                />
              </>
            )}
            <Button
              type="link"
              onClick={() => navigate(`/admin/orders/details/${record.id}`)}
            >
              View
            </Button>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/orders/edit/${record.id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete the order"
              description="Are you sure to delete this order?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // const filtered = orders.filter((o) => {
  //   const q = query.toLowerCase().trim();
  //   if (!q) return true;
  //   const bde = bdes.find(u => u.id === o.bdeId);
  //   const bdeName = String(bde ? bde.name : o.bdeId || '').toLowerCase();
  //   const id = String(o.id || '').toLowerCase();
  //   const name = String(o.customerName || '').toLowerCase();
  //   const status = String(o.status || '').toLowerCase();
  //   const date = String(o.createdAt || '').toLowerCase();
  //   return (
  //     bdeName.includes(q) ||
  //     id.includes(q) ||
  //     name.includes(q) ||
  //     status.includes(q) ||
  //     date.includes(q)
  //   );
  // });

  const filtered = orders.filter((o) => {
    const q = query.toLowerCase().trim();

    const bde = bdes.find(u => u.id === o.bdeId);

    const matchSearch =
      !q ||
      String(o.id || '').toLowerCase().includes(q) ||
      String(o.customerName || '').toLowerCase().includes(q) ||
      String(o.status || '').toLowerCase().includes(q) ||
      String(o.createdAt || '').toLowerCase().includes(q) ||
      String(bde ? bde.name : o.bdeId || '').toLowerCase().includes(q);

    const matchStatus =
      !statusFilter || o.status === statusFilter;

    return matchSearch && matchStatus;
  });

  const screens = Grid.useBreakpoint();



  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
        <h2>All Orders</h2>
        <Space>
          <Button icon={<FileExcelOutlined />} onClick={handleExport}>
            Export to Excel
          </Button>

          <Select
            placeholder="Filter by Status"
            allowClear
            style={{ width: 180 }}
            value={statusFilter || undefined}
            onChange={(value) => setStatusFilter(value)}
          >
            <Select.Option value="Pending">Pending</Select.Option>
            <Select.Option value="Processing">Processing</Select.Option>
            <Select.Option value="Completed">Completed</Select.Option>
            <Select.Option value="Expected">Expected</Select.Option>
            <Select.Option value="Cancelled">Cancelled</Select.Option>
          </Select>

          <Input.Search
            allowClear
            placeholder="Search orders"
            style={{ width: 320 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Space>
      </div>

      {!screens.md ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map(order => {
            let color = 'default';
            if (order.status === 'Completed') color = 'green';
            if (order.status === 'Pending') color = 'orange';
            if (order.status === 'Processing') color = 'blue';
            if (order.status === 'Cancelled') color = 'red';

            const bde = bdes.find(u => u.id === order.bdeId);
            const shop = shops.find(s => s.id === order.shopId);
            const showInvoice = order.status === 'Completed';

            return (
              <Card key={order.id} size="small" title={`#${order.id}`} extra={<Tag color={color}>{order.status}</Tag>}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Shop Name:</span>
                    <span style={{ fontWeight: 500 }}>{order.customerName}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Amount:</span>
                    <span style={{ fontWeight: 600 }}>₹{Number(order.amount).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Date:</span>
                    <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>BDE:</span>
                    <span>{bde ? bde.name : order.bdeId || 'N/A'}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                    {showInvoice && (
                      <>
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleSinglePay(order)}
                          disabled={order.commissionPaid}
                        >
                          {order.commissionPaid ? "Paid" : "Pay"}
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={invoiceLoading === order.id}
                          onClick={() => handleInvoice(order, shop, bde, 'download')}
                        >
                          Invoice
                        </Button>
                      </>

                    )}
                    <Button type="link" size="small" onClick={() => navigate(`/admin/orders/details/${order.id}`)}>View</Button>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => navigate(`/admin/orders/edit/${order.id}`)}>Edit</Button>
                    <Popconfirm
                      title="Delete the order"
                      description="Are you sure?"
                      onConfirm={() => handleDelete(order.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button type="link" danger size="small" icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onDoubleClick: () => navigate(`/admin/orders/details/${record.id}`),
          })}
        />
      )}
    </div>
  );
};

export default Orders;
